import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { format } from 'date-fns';
import { ConditionOptionGroup, FieldType } from '@frontend/app/types/globalTypes';
import { GetNodeOptions_getNodeOptions_conditionOptions } from '@frontend/app/queries/types/GetNodeOptions';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, CardContent } from '@/shadcn/components/ui/card';
import { Button } from '@/shadcn/components/ui/button';
import { TooltipProvider } from '@/shadcn/components/ui/tooltip';
import {
  CustomFieldUpdate,
  CustomFieldUpdateBlueprint,
  AvailableCustomField,
} from './types';
import { CustomFieldInputRow } from './CustomFieldInputRow';

type UICustomFieldUpdate = CustomFieldUpdate & { isEditing?: boolean; isSaved?: boolean };

interface CustomFieldUpdaterProps {
  conditionOptions: GetNodeOptions_getNodeOptions_conditionOptions[];
  initialUpdates?: CustomFieldUpdateBlueprint[]; // Changed to accept simplified blueprints
  onUpdatesChange: (updates: CustomFieldUpdateBlueprint[]) => void; // Changed to output simplified blueprints
}

export const CustomFieldUpdater: React.FC<CustomFieldUpdaterProps> = ({
  conditionOptions,
  initialUpdates = [],
  onUpdatesChange,
}) => {
  // Convert simplified blueprint to full UI structure using conditionOptions
  const blueprintToFullUpdate = useCallback((blueprint: CustomFieldUpdateBlueprint, availableFields: AvailableCustomField[]): CustomFieldUpdate => {
    const field = availableFields.find((f) => f.id === blueprint.fieldId);
    if (!field) {
      throw new Error(`Field with ID ${blueprint.fieldId} not found in conditionOptions`);
    }

    return {
      id: uuidv4(),
      fieldId: blueprint.fieldId,
      fieldValue: blueprint.fieldValue,
      attributeType: field.attributeType,
      displayName: field.displayName, // Always use field displayName from API
      choices: field.choices,
    };
  }, []);

  // Convert full UI structure to simplified blueprint
  const fullUpdateToBlueprint = useCallback((update: CustomFieldUpdate): CustomFieldUpdateBlueprint => {
    return {
      fieldId: update.fieldId,
      fieldValue: update.fieldValue,
    };
  }, []);
  const [state, setState] = useState<{
    updates: UICustomFieldUpdate[];
    availableFields: AvailableCustomField[];
    searchQuery: string;
    isLoading: boolean;
    errors: Record<string, string>;
  }>({
    updates: [], // Will be initialized after availableFields are loaded
    availableFields: [],
    searchQuery: '',
    isLoading: false,
    errors: {},
  });

  // Track if we've already initialized to prevent infinite loops
  const isInitializedRef = useRef(false);
  const lastOutputRef = useRef<string>('');

  // Filter custom member data fields from condition options
  const availableFields = useMemo((): AvailableCustomField[] => {
    const fields = conditionOptions
      .filter((option) => option.group === ConditionOptionGroup.CUSTOM_MEMBER_DATA)
      .map((option) => ({
        id: option.id,
        displayName: option.displayName,
        attributeType: option.attributeType,
        choices: option.choices || null,
        defaultValue: option.defaultValue,
      }));

    return fields;
  }, [conditionOptions]);

  // Update available fields and initialize updates from blueprints
  useEffect(() => {
    if (availableFields.length === 0) return; // Wait for fields to load

    setState((prev) => {
      // Only initialize once
      if (isInitializedRef.current) {
        // Already initialized, just update available fields
        return {
          ...prev,
          availableFields,
        };
      }

      let updates = prev.updates;

      if (initialUpdates.length > 0) {
        // Convert initial blueprint updates to full updates using the available fields
        const initialFullUpdates = initialUpdates.map((blueprint) => {
          try {
            return blueprintToFullUpdate(blueprint, availableFields);
          } catch (error) {
            console.error('Failed to convert blueprint to full update:', error);
            return null;
          }
        }).filter((update): update is CustomFieldUpdate => update !== null);

        updates = initialFullUpdates;
      }

      // Mark as initialized regardless of whether there were initial updates
      isInitializedRef.current = true;

      return {
        ...prev,
        availableFields,
        updates,
      };
    });
  }, [availableFields, blueprintToFullUpdate, initialUpdates]);

  // Validate individual update
  const validateUpdate = useCallback((update: CustomFieldUpdate): boolean => {
    if (!update.fieldId) {
      return false;
    }

    // Check if value exists (allow 0, false, and empty string for TEXT fields)
    if (update.fieldValue === undefined || update.fieldValue === null) {
      return false;
    }

    // For TEXT fields, allow empty strings
    if (update.attributeType === FieldType.TEXT && update.fieldValue === '') {
      return true;
    }

    // Validate based on field type using FieldType enum
    switch (update.attributeType) {
      case FieldType.DATE:
        // Accept Date objects, valid date strings, or ISO strings
        if (update.fieldValue instanceof Date ||
            (typeof update.fieldValue === 'string' && update.fieldValue !== '' && !isNaN(Date.parse(update.fieldValue)))) {
          return true;
        }
        return false;

      case FieldType.ARRAY:
        if (Array.isArray(update.fieldValue) && update.fieldValue.length > 0) {
          return true;
        }
        return false;

      case FieldType.BOOLEAN:
        if (typeof update.fieldValue === 'boolean') {
          return true;
        }
        return false;

      case FieldType.NUMBER: {
        // Accept both numbers and strings that can be converted to numbers
        const isValidNumber =
          (typeof update.fieldValue === 'number' && !isNaN(update.fieldValue as number)) ||
          (typeof update.fieldValue === 'string' && update.fieldValue !== '' && !isNaN(Number(update.fieldValue)));
        return isValidNumber;
      }

      case FieldType.TEXT:
      default:
        // For TEXT fields with choices (single select), require non-empty value
        const field = availableFields.find((f) => f.id === update.fieldId);
        if (field?.choices && field.choices.length > 0) {
          return typeof update.fieldValue === 'string' && update.fieldValue !== '';
        }
        // For free text fields, require non-empty string
        if (typeof update.fieldValue === 'string') {
          return update.fieldValue.trim() !== '';
        }
        // Also accept numbers and booleans that can be converted to strings for select fields
        if (update.fieldValue !== null && update.fieldValue !== undefined) {
          return true;
        }
        return false;
    }
  }, [availableFields]);

  // Notify parent of changes - convert to simplified blueprints
  useEffect(() => {
    // Only notify after initialization is complete
    if (!isInitializedRef.current) {
      return;
    }

    const validUpdates = state.updates.filter((update) => {
      const isValid = validateUpdate(update);
      return isValid;
    });
    const blueprints = validUpdates.map(fullUpdateToBlueprint);

    // Only call parent if output has actually changed
    const outputString = JSON.stringify(blueprints);
    if (lastOutputRef.current !== outputString) {
      lastOutputRef.current = outputString;
      onUpdatesChange(blueprints);
    }
  }, [state.updates, validateUpdate, fullUpdateToBlueprint, onUpdatesChange]);

  // Add a new custom field update
  const addCustomFieldUpdate = () => {
    const newUpdate: CustomFieldUpdate = {
      id: uuidv4(),
      fieldId: '',
      fieldValue: '',
      attributeType: FieldType.TEXT,
      displayName: '',
      choices: undefined,
    };

    setState((prev) => ({
      ...prev,
      updates: [...prev.updates, newUpdate],
    }));
  };

  // Update a specific custom field update
  const updateCustomFieldUpdate = (updateId: string, updatedField: UICustomFieldUpdate) => {
    setState((prev) => ({
      ...prev,
      updates: prev.updates.map((update) =>
        (update.id === updateId ? updatedField : update)
      ),
    }));
  };

  // Remove a custom field update
  const removeCustomFieldUpdate = (updateId: string) => {
    setState((prev) => ({
      ...prev,
      updates: prev.updates.filter((update) => update.id !== updateId),
    }));
  };

  // Save a specific update (validation passed)
  const saveUpdate = (updateId: string, updatedData?: UICustomFieldUpdate) => {
    // Use the provided update data or find it in state
    const update = updatedData || state.updates.find((u) => u.id === updateId);

    if (update && validateUpdate(update)) {
      // Mark as successfully saved - remove any temporary editing flags
      setState((prev) => {
        const { [updateId]: _removed, ...restErrors } = prev.errors;
        const newState = {
          ...prev,
          updates: prev.updates.map((u) =>
            (u.id === updateId
              ? { ...update, isEditing: false, isSaved: true }
              : u)
          ),
          errors: restErrors,
        };
        return newState;
      });
    } else {
      // Show validation error for this specific update
      setState((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          [updateId]: !update?.fieldId
            ? 'Field ID is not defined or ID is missing'
            : 'Please enter a valid value for this field'
        }
      }));
    }
  };

  // Cancel editing a specific update
  const cancelUpdate = (updateId: string) => {
    // For new updates that haven't been configured, remove them
    const update = state.updates.find((u) => u.id === updateId);
    if (update && !update.fieldId) {
      removeCustomFieldUpdate(updateId);
    }
    // For existing updates, could restore original values
  };

  // Edit an existing update
  const editUpdate = (updateId: string) => {
    setState((prev) => ({
      ...prev,
      updates: prev.updates.map((update) =>
        (update.id === updateId
          ? { ...update, isEditing: true, isSaved: false }
          : update)
      ),
      errors: (() => {
        const { [updateId]: _removed, ...restErrors } = prev.errors;
        return restErrors;
      })()
    }));
  };

  // Render summary of configured custom field updates
  const renderCustomFieldSummary = (update: UICustomFieldUpdate) => {
    const field = availableFields.find((f) => f.id === update.fieldId);
    if (!field || !validateUpdate(update)) return null;

    let valueDisplay = update.fieldValue;

    // Format value display based on field type
    if (update.attributeType === FieldType.BOOLEAN) {
      valueDisplay = update.fieldValue ? 'Yes' : 'No';
    } else if (update.attributeType === FieldType.ARRAY && Array.isArray(update.fieldValue)) {
      const selectedChoices = update.fieldValue
        .map((val) => field.choices?.find((c) => c.value === val)?.displayName || val)
        .join(', ');
      valueDisplay = selectedChoices;
    } else if (update.attributeType === FieldType.DATE && update.fieldValue) {
      valueDisplay = format(new Date(update.fieldValue), 'MMM do, yyyy');
    } else if (field.choices && update.fieldValue) {
      const choice = field.choices.find((c) => c.value === update.fieldValue);
      valueDisplay = choice?.displayName || update.fieldValue;
    }

    return (
      <Card key={`summary-${update.id}`} className="group bg-grey-1 rounded-lg border-none shadow-none hover:bg-grey-2 transition-colors relative">
        <CardContent className="p-3">
          <div className="text-sm text-grey-6 pr-16 group-hover:opacity-90 transition-opacity">
            Update {field.displayName} to {valueDisplay}
          </div>

          {/* Hover actions with background */}
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editUpdate(update.id)}
              className="w-6 h-6 p-0 bg-white/80 hover:bg-white border border-grey-2 shadow-sm backdrop-blur-sm"
            >
              <FontAwesomeIcon icon={faPen} className="text-grey-5 hover:text-grey-6" size="xs" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => removeCustomFieldUpdate(update.id)}
              className="w-6 h-6 p-0 bg-white/80 hover:bg-red-50 border border-grey-2 shadow-sm backdrop-blur-sm"
              aria-label="Delete custom field update"
            >
              <Trash2 className="w-3 h-3 text-grey-5 hover:text-red-600" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Check if there are any updates currently being edited
  const hasActiveEditing = state.updates.some((update) => {
    const isConfigured = validateUpdate(update);
    const isEditing = (update as any).isEditing;
    const isSaved = (update as any).isSaved;
    return (!isConfigured || isEditing) && !isSaved;
  });

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Render summary of completed updates */}
        {state.updates
        .filter((update) => {
          const isCompleted = validateUpdate(update);
          const isEditing = (update as any).isEditing;
          return isCompleted && !isEditing;
        })
        .map(renderCustomFieldSummary)}

        {/* Render active/editing custom field inputs */}
        {state.updates.map((update) => {
          const isConfigured = validateUpdate(update);
          const isEditing = (update as any).isEditing;
          const isSaved = (update as any).isSaved;

          // Show input row for unconfigured updates or when editing (but not if already saved)
          if ((!isConfigured || isEditing) && !isSaved) {
            // Get list of field IDs already used by other saved/configured updates
            const usedFieldIds = state.updates
              .filter((u) => u.id !== update.id && validateUpdate(u) && (u as any).isSaved) // Only include saved updates
              .map((u) => u.fieldId)
              .filter(Boolean); // Remove empty field IDs

            return (
              <CustomFieldInputRow
                key={update.id}
                update={update}
                availableFields={availableFields}
                usedFieldIds={usedFieldIds}
                onUpdateField={(updatedField) => updateCustomFieldUpdate(update.id, updatedField)}
                onSave={(savedUpdate) => {
                  // savedUpdate contains the complete validated data from CustomFieldInputRow
                  if (savedUpdate) {
                    saveUpdate(update.id, savedUpdate);
                  } else {
                    saveUpdate(update.id);
                  }
                }}
                onCancel={() => {
                  if (isEditing) {
                    // Remove isEditing flag when canceling edit
                    const cleanUpdate = { ...update };
                    delete (cleanUpdate as any).isEditing;
                    updateCustomFieldUpdate(update.id, cleanUpdate);
                  } else {
                    cancelUpdate(update.id);
                  }
                }}
                onDelete={() => removeCustomFieldUpdate(update.id)}
                isValid={false} // Don't show premature validation - let the row handle its own validation
              />
            );
          }

          return null;
        })}

        {/* Show error messages for failed saves */}
        {Object.entries(state.errors).map(([updateId, error]) => {
        if (!error) return null;
        const update = state.updates.find((u) => u.id === updateId);
        if (!update) return null;

        return (
          <Card key={`error-${updateId}`} className="bg-red-50 border-red-200 rounded-lg">
            <CardContent className="p-3">
              <div className="text-sm text-red-600">
                <strong>Save failed:</strong> {error}
              </div>
            </CardContent>
          </Card>
        );
      })}

        {/* Add More Button - Only show when not editing */}
        {!hasActiveEditing && (
          <Card className="bg-white rounded-lg border-[1px] border-dashed border-grey-3 shadow-none hover:bg-grey-1 hover:border-grey-2">
            <CardContent className="p-0">
              <Button
                variant="ghost"
                onClick={addCustomFieldUpdate}
                className="w-full"
                aria-label="Add custom field update"
              >
                <Plus className="w-4 h-4" />
                Add Field
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Validation feedback */}
        {state.updates.length === 0 && (
          <p className="text-sm text-grey-4 text-center">
            No custom field updates configured
          </p>
        )}
      </div>
    </TooltipProvider>
  );
};
